import { inject, Injectable } from '@angular/core';
import { arrayAdd, arrayRemove, arrayUpdate } from '@datorama/akita';
import dayjs from 'dayjs';
import { omit, uniqBy } from 'lodash-es';
import { firstValueFrom, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { EMPTY_UUID } from '@shared/constants/uuid';
import {
  CreateTimelineMilestoneInput,
  createTimelineMilestoneMutation,
  EntityType,
  EventType,
  GqlService,
  listTimelineMilestonesQuery,
  UpdateTimelineMilestoneInput,
  updateTimelineMilestoneMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { MilestoneModel, MilestoneStore } from './milestone.store';
import { MilestoneCategoryStore } from '../milestone-category.store';
import { TimelineStore } from './timeline.store';
import { MilestoneCategoryQuery } from '../milestone-category.query';

export type TimelineBannerStatus = 'error' | 'warning' | undefined;

export interface TimelineGridData {
  actual_end_date: null | string;
  actual_start_date: null | string;
  contract_end_date: string;
  contract_month_difference: null | number;
  contract_start_date: string;
  description: null | string;
  id: string;
  milestone_category_id: string;
  milestone_category_name: string;
  milestone_id: string;
  milestone_name: string;
  organizations_with_forecasts: { id: string; name: string }[];
  revised_end_date: null | string;
  revised_start_date: null | string;
  track_from_milestone_id: null | string;
  track_from_milestone_name: string;
}

export type UpdateDependencyInput = {
  contract_end_date: string;
  contract_start_date: string;
  field_name: string;
  id: string;
  track_from_milestone_id: null | string;
};

export type UpdateTimelineMilestone = Omit<
  UpdateTimelineMilestoneInput,
  'description' | 'milestone_category_id' | 'name'
> & {
  description: null | string;
  milestone_category_id: string;
  milestone_id: string;
  name: string;
};

@Injectable({ providedIn: 'root' })
export class TimelineService {
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private milestoneCategoryQuery = inject(MilestoneCategoryQuery);
  private milestoneCategoryStore = inject(MilestoneCategoryStore);
  private milestoneStore = inject(MilestoneStore);
  private overlayService = inject(OverlayService);
  private timelineStore = inject(TimelineStore);

  async calculateTimelineBanner(
    timelines: listTimelineMilestonesQuery[],
    currentOpenMonth: string
  ): Promise<TimelineBannerStatus> {
    const sortedTimelines = [...timelines];

    sortedTimelines.sort((tl, tl2) => {
      const date1 = dayjs(tl.contract_end_date);
      const date2 = dayjs(tl2.contract_end_date);

      return dayjs(date1).isAfter(date2) ? -1 : dayjs(date2).isAfter(date1) ? 1 : 0;
    });
    const lastMonthOfTrial = dayjs(sortedTimelines[0].contract_end_date).format('YYYY-MM-01');
    const openMonth = dayjs(currentOpenMonth).format('YYYY-MM-01');
    const numDiff = dayjs(lastMonthOfTrial).diff(openMonth, 'month');

    if (numDiff > 2) {
      return undefined;
    } else if (numDiff <= 2 && numDiff > 0) {
      return 'warning';
    } else {
      // If the current month is the last month of any timeline we can not differantiate whether current month was closed or not
      // by using only getOpenMonth, we need to look at it from a different call
      const resp = await firstValueFrom(this.gqlService.listTrialVendorTimeline$());

      if (resp.success && resp.data) {
        const isCurrentMonthClosed = !!resp.data.find(
          (vt) => vt.month_close_state === 'CLOSED' && vt.timeline_month === lastMonthOfTrial
        );

        return isCurrentMonthClosed ? 'error' : 'warning';
      } else {
        return 'error';
      }
    }
  }

  checkIfTimelineMilestoneExist() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.timelineStore.setLoading(true);

        return this.gqlService.getTimeline$();
      }),
      switchMap(({ data: timeline, errors, success }) => {
        if (timeline && success) {
          return this.gqlService.listTimelineMilestoneIds$(timeline.id).pipe(
            tap(({ data: milestones }) => {
              this.timelineStore.update({
                timelineExists: !!milestones?.length,
              });
            })
          );
        }

        this.overlayService.error(errors);
        this.timelineStore.setLoading(false);

        return of();
      })
    );
  }

  convertAnyToUpdateInput(
    item: UpdateDependencyInput | UpdateTimelineMilestone
  ): UpdateTimelineMilestoneInput {
    return item;
  }

  convertToCreateInput(item: TimelineGridData): Omit<CreateTimelineMilestoneInput, 'timeline_id'> {
    const omitFields = [
      'milestone_name',
      'milestone_category_name',
      'organizations_with_forecasts',
      'track_from_milestone_name',
      'contract_month_difference',
    ];

    return {
      contract_end_date: item.contract_end_date || '',
      contract_start_date: item.contract_start_date || '',
      milestone_category_id: item.milestone_category_id || '',
      name: item.milestone_name,
      ...omit(item, omitFields),
    };
  }

  convertToUpdateInput(item: TimelineGridData): UpdateTimelineMilestoneInput {
    return {
      contract_end_date: item.contract_end_date,
      contract_start_date: item.contract_start_date,
      description: item.description,
      id: item.id,
      milestone_category_id: item.milestone_category_id,
      name: item.milestone_name,
      track_from_milestone_id: item.track_from_milestone_id || EMPTY_UUID,
    };
  }

  createTimelineMilestone(input: Omit<CreateTimelineMilestoneInput, 'timeline_id'>) {
    const { items } = this.timelineStore.getValue();
    const timelineMilestoneId = Utils.uuid();
    const milestoneId = Utils.uuid();

    this.milestoneStore.add({
      __typename: 'Milestone',
      description: input.description,
      id: milestoneId,
      milestone_category_id: input.milestone_category_id,
      name: input.name,
    } as MilestoneModel);
    const data: createTimelineMilestoneMutation = {
      __typename: 'TimelineMilestone',
      actual_end_date: input.actual_end_date,
      actual_start_date: input.actual_end_date,
      contract_end_date: input.contract_end_date,
      contract_start_date: input.contract_start_date,
      id: timelineMilestoneId,
      milestone: {
        __typename: 'Milestone',
        id: milestoneId,
        milestone_category: {
          __typename: 'MilestoneCategory',
          id: this.milestoneCategoryQuery.getEntity(input.milestone_category_id)?.id || '',
          name: this.milestoneCategoryQuery.getEntity(input.milestone_category_id)?.name || '',
        },
        milestone_category_id: input.milestone_category_id,
        name: input.name,
        organizations_with_forecasts: [],
      },
      revised_end_date: input.revised_end_date,
      revised_start_date: input.revised_start_date,
      track_from_milestone: input.track_from_milestone_id
        ? {
            __typename: 'Milestone',
            id: input.track_from_milestone_id,
          }
        : null,
    };

    this.timelineStore.update({ items: [...items, data] });

    return { createdMilestoneId: data.id, milestoneId, success: true };
  }

  deleteTimelineMilestone(id: string) {
    this.timelineStore.setLoading(true);

    this.timelineStore.update((x) => {
      const milestone_id = x.items.find((ti) => {
        return ti.id === id;
      })?.milestone?.id;

      const ids: string[] = [];

      x.items.forEach((ti) => {
        if (ti.track_from_milestone?.id === milestone_id) {
          ids.push(ti.id);
        }
      });

      return {
        items: arrayUpdate(arrayRemove(x.items, [id]), ids, {
          track_from_milestone: null,
        }),
      };
    });
    this.timelineStore.setLoading(false);
  }

  getTimelineItems() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.timelineStore.setLoading(true);

        return this.gqlService.getTimeline$().pipe(
          switchMap(({ data, errors, success }) => {
            if (success && data) {
              return this.gqlService.listTimelineMilestones$(data.id).pipe(
                tap((x) => {
                  if (x.data) {
                    this.timelineStore.update({
                      items: x.data,
                      selectedTimeline: data.id,
                      timelineExists: true,
                    });
                  }

                  this.timelineStore.setLoading(false);
                })
              );
            }

            this.overlayService.error(errors);
            this.timelineStore.setLoading(false);

            return of(<GraphqlResponse<listTimelineMilestonesQuery[]>>{
              data: null,
              errors: [],
              success: false,
            });
          })
        );
      })
    );
  }

  // Use this function to make sequential requests for the
  // update, updateDeps, and delete inputs in saveChanges().
  async requestChanges<T>(inputs: Promise<GraphqlResponse<T>>[], errors: string[]): Promise<void> {
    const results = await batchPromises(inputs, (p) => p);

    results.forEach((r) => {
      if (!(r instanceof Error) && r.errors && r.errors.length > 0) {
        errors.push(...r.errors);
      }
    });
  }

  async saveChanges(
    createInputs: Omit<CreateTimelineMilestoneInput, 'timeline_id'>[],
    updateInputs: UpdateTimelineMilestone[],
    updateDependencyInputs: UpdateDependencyInput[],
    deleteInputs: string[],
    gridData: TimelineGridData[]
  ) {
    const { selectedTimeline } = this.timelineStore.getValue();

    const createProms: GraphqlResponse<createTimelineMilestoneMutation>[] = [];
    const updateProms: Promise<GraphqlResponse<unknown>>[] = [];
    const deleteProms: Promise<GraphqlResponse<unknown>>[] = [];
    const errors: string[] = [];

    // Prevent milestones from being created
    // if they're eventually deleted
    const createInputsFiltered = createInputs.filter((input) => {
      return input.id ? !deleteInputs.includes(input.id) : false;
    });

    // Use the latest grid data to create the milestone
    const createInputsMapped = createInputsFiltered.map((input) => {
      const data = gridData.find((row) => row.id === input.id);

      return data ? this.convertToCreateInput(data) : input;
    });

    /**
    Create
    */

    for (const createInput of createInputsMapped) {
      const result = await firstValueFrom(
        this.gqlService.createTimelineMilestone$({
          ...createInput,
          timeline_id: selectedTimeline,
        })
      );

      createProms.push(result);
    }

    const updateInputsJoined = [...updateInputs, ...updateDependencyInputs];

    createProms.forEach((res) => {
      if (res instanceof Error || !res.data) {
        return;
      }
    });

    const updateInputsFiltered = updateInputsJoined.filter((input) => {
      return input.id ? !deleteInputs.includes(input.id) : false;
    });

    const updateInputsDeduped = uniqBy(updateInputsFiltered, 'id');

    const updateInputsMapped = updateInputsDeduped.map((input) => {
      const data = gridData.find((row) => row.id === input.id);

      return data ? this.convertToUpdateInput(data) : this.convertAnyToUpdateInput(input);
    });

    updateInputsMapped.forEach((updateInput) => {
      updateProms.push(firstValueFrom(this.gqlService.updateTimelineMilestone$(updateInput)));
    });

    await this.requestChanges(updateProms, errors);

    /**
    Delete
    */

    deleteInputs.forEach((deleteInput) => {
      deleteProms.push(firstValueFrom(this.gqlService.removeTimelineItem$(deleteInput)));
    });

    await this.requestChanges(deleteProms, errors);

    // The errors array is passed by reference to requestChanges(),
    // so the array will already contain all the update, updateDeps, and
    // delete errors here
    if (errors.length > 0) {
      return { errors, success: false };
    }

    return this.triggerTimelineUpdatedEvent();
  }

  triggerTimelineUpdatedEvent() {
    return firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getValue().trialKey,
        entity_type: EntityType.TRIAL,
        type: EventType.TIMELINE_UPDATED,
      })
    );
  }

  updateTimelineForDependency({
    contract_end_date,
    contract_start_date,
    id,
    track_from_milestone_id,
  }: UpdateDependencyInput) {
    const { items } = this.timelineStore.getValue();
    const timelineMilestone = items.find((tm) => tm.id === id);

    if (timelineMilestone) {
      const data: updateTimelineMilestoneMutation = {
        __typename: 'TimelineMilestone',
        contract_end_date,
        contract_start_date,
        id,
        milestone: timelineMilestone.milestone,
        track_from_milestone: track_from_milestone_id
          ? {
              __typename: 'Milestone',
              id: track_from_milestone_id,
            }
          : null,
      };

      this.timelineStore.update((x) => {
        return {
          items: arrayUpdate(x.items, id, data),
        };
      });
    }

    return {
      success: true,
    };
  }

  updateTimelineMilestone(input: UpdateTimelineMilestone) {
    this.timelineStore.setLoading(true);
    const {
      contract_end_date,
      contract_start_date,
      description,
      id,
      milestone_category_id,
      milestone_id,
      name,
      track_from_milestone_id,
    } = input;

    this.milestoneStore.update(milestone_id, (state) => {
      // if the milestone_category_id changed
      if (state.milestone_category_id !== milestone_category_id) {
        // remove the milestone_id from old category
        this.milestoneCategoryStore.update(state.milestone_category_id, ({ milestone_ids }) => {
          return { milestone_ids: arrayRemove(milestone_ids, milestone_id) };
        });

        // add milestone_id to new category
        this.milestoneCategoryStore.update(milestone_category_id, ({ milestone_ids }) => {
          return { milestone_ids: arrayAdd(milestone_ids, milestone_id) };
        });
      }

      return {
        description,
        milestone_category_id,
        name,
      };
    });

    const { items } = this.timelineStore.getValue();
    const timelineMilestone = items.find((tm) => tm.id === id);

    if (timelineMilestone) {
      const data: updateTimelineMilestoneMutation & { contract_month_difference: null | string } = {
        __typename: 'TimelineMilestone',
        contract_end_date: contract_end_date || '',
        contract_month_difference: '',
        contract_start_date: contract_start_date || '',
        id,
        milestone: {
          __typename: 'Milestone',
          id: milestone_id,
          milestone_category: {
            __typename: 'MilestoneCategory',
            name: this.milestoneCategoryQuery.getEntity(input.milestone_category_id)?.name || '',
          },
          milestone_category_id,
          name,
          organizations_with_forecasts: timelineMilestone.milestone.organizations_with_forecasts,
        },
        track_from_milestone: track_from_milestone_id
          ? {
              __typename: 'Milestone',
              id: track_from_milestone_id,
            }
          : null,
      };

      this.timelineStore.update((x) => {
        return {
          items: arrayUpdate(x.items, id, data),
        };
      });
    }

    this.timelineStore.setLoading(false);

    return {
      success: true,
    };
  }
}
